//@flow

import {useState, useEffect, useMemo} from 'react';
import {
  getCustomerComplaintsService,
  updateCustomerComplaintService,
} from './promises';

import type {
  CustomerComplaint,
  CustomerComplaintState,
  PaginationRequest,
  PaginationResponse,
  Pagination,
} from '../../entities';

type CustomerComplaintFilter = {
  state?: CustomerComplaintState,
  dateFrom?: string,
  dateTo?: string,
  search?: string,
};

export function useCustomerComplaints() {
  const [complaints, setComplaints] = useState<CustomerComplaint[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [filter, setFilter] = useState<CustomerComplaintFilter>({});
  const [pagination, setPagination] = useState<Pagination>({
    page: 0,
    size: 10,
    total: 0,
  });

  const loadComplaints = async () => {
    setLoading(true);
    try {
      const params: PaginationRequest = {
        page: pagination.page,
        size: pagination.size,
      };

      const response: PaginationResponse<CustomerComplaint> = await getCustomerComplaintsService(
        params
      );

      setComplaints(response.data);
      if (response.pagination) {
        setPagination(response.pagination);
      }
    } catch (error) {
      console.error('Error loading customer complaints:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load complaints on mount and when pagination changes
  useEffect(() => {
    loadComplaints();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, pagination.size]);

  const filteredComplaints = useMemo((): CustomerComplaint[] => {
    let result: CustomerComplaint[] = [...complaints];

    if (filter.state) {
      result = result.filter(
        (complaint: CustomerComplaint) => complaint.state === filter.state
      );
    }

    if (filter.dateFrom) {
      result = result.filter(
        (complaint: CustomerComplaint) =>
          complaint.createdAt >= (filter.dateFrom || '')
      );
    }

    if (filter.dateTo) {
      result = result.filter(
        (complaint: CustomerComplaint) =>
          complaint.createdAt <= (filter.dateTo || '')
      );
    }

    if (filter.search) {
      const searchLower = filter.search.toLowerCase();
      result = result.filter(
        (complaint: CustomerComplaint) =>
          complaint.email.toLowerCase().includes(searchLower) ||
          complaint.phone.includes(filter.search || '') ||
          complaint.identifier.toLowerCase().includes(searchLower)
      );
    }

    return result;
  }, [complaints, filter]);

  const updateComplaintStatus = async (
    complaintId: string,
    newState: CustomerComplaintState
  ) => {
    try {
      const updatedComplaint = await updateCustomerComplaintService({
        customerComplaintId: complaintId,
        state: newState,
      });

      setComplaints(prev =>
        prev.map(complaint =>
          complaint.id === complaintId ? updatedComplaint : complaint
        )
      );
    } catch (error) {
      console.error('Error updating complaint status:', error);
      throw error;
    }
  };

  const exportComplaints = () => {
    console.log('Exporting customer complaints...', filteredComplaints);
    // Here you would typically call an API to export the data
  };

  // Check if complaint is overdue (more than 10 days in RECEIVED status)
  const isComplaintOverdue = (complaint: CustomerComplaint): boolean => {
    if (complaint.state !== 'RECEIVED') return false;

    const today = new Date();
    const createdDate = new Date(complaint.createdAt);
    const daysDiff = Math.floor(
      (today.getTime() - createdDate.getTime()) / (1000 * 60 * 60 * 24)
    );
    return daysDiff > 10;
  };

  const changePage = (newPage: number) => {
    setPagination(prev => ({
      ...prev,
      page: newPage,
    }));
  };

  const changePageSize = (newSize: number) => {
    setPagination(prev => ({
      ...prev,
      size: newSize,
      page: 0,
    }));
  };

  return {
    complaints: filteredComplaints,
    allComplaints: filteredComplaints,
    loading,
    filter,
    pagination,
    setFilter,
    updateComplaintStatus,
    exportComplaints,
    isComplaintOverdue,
    changePage,
    changePageSize,
    refetch: loadComplaints,
  };
}
